import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useFormikContext } from "formik";
import { constants } from "@qgiv/core-js";
import { CardGroup, radioKeyboardEventHandler } from "@qgiv/core-donor";
import ConnectedIndividualRoleCard from "./ConnectedIndividualRoleCard";
import DetectTeamRoleCard from "./DetectTeamRoleCard";

const detectRoleSelected = (Registration_Role) =>
    !!(
        Registration_Role &&
        Registration_Role[0] &&
        Registration_Role[0].length > 0
    );

const DetectRoleCard = ({ entityType, ...props }) => {
    const { ENUMS } = constants;
    const {
        EntityType: { REGISTRATION, TEAM },
    } = ENUMS;

    const getRoleCard = () => {
        switch (entityType) {
            case REGISTRATION:
                return <ConnectedIndividualRoleCard {...props} />;
            case TEAM: {
                return <DetectTeamRoleCard {...props} />;
            }
            default:
                return null;
        }
    };
    return <>{getRoleCard()}</>;
};

DetectRoleCard.propTypes = {
    // Entity type enum used for the value of the registration role card input
    entityType: PropTypes.number.isRequired,
};

const RegistrationRole = ({
    controlId,
    isHidden = false,
    roles = [],
    teamModalOpen,
}) => {
    const { values } = useFormikContext();
    const { ENUMS } = constants;
    const {
        EntityType: { REGISTRATION, TEAM },
    } = ENUMS;
    const { Registration_Role, Team } = values;

    const [roleSelected, setRoleSelected] = useState(
        detectRoleSelected(Registration_Role),
    );

    useEffect(() => {
        setRoleSelected(detectRoleSelected(Registration_Role));
    }, [Registration_Role]);

    const [teamModalWasOpen, setTeamModalWasOpen] = useState(false);
    const registrationRefs = roles.map(() => React.createRef());

    const handleKeyDown = (event, index) => {
        if (Registration_Role.length > 0 || roles.length <= 1) return;
        radioKeyboardEventHandler(event, index, registrationRefs);
    };
    // When role is selected, we want to have focus on current registration role card for tab accessibility
    useEffect(() => {
        if (teamModalOpen) {
            return;
        }
        if (roleSelected) {
            const selectedRole = Registration_Role[0];
            const selectedRefIndex = roles
                .map((role) => role.value)
                .indexOf(selectedRole);

            if (
                selectedRefIndex > -1 &&
                registrationRefs[selectedRefIndex] &&
                registrationRefs[selectedRefIndex].current
            ) {
                switch (Number(selectedRole)) {
                    case REGISTRATION:
                        registrationRefs[selectedRefIndex].current.focus();
                        break;
                    case TEAM:
                        if (Team) {
                            registrationRefs[selectedRefIndex].current.focus();
                        }
                        break;
                    default:
                        break;
                }
            }
        } else {
            // focus on team reg role if team modal closed without selecting/creating a team
            // eslint-disable-next-line no-lonely-if
            if (!teamModalOpen && teamModalWasOpen) {
                setTeamModalWasOpen(false);

                const teamRefIndex = roles
                    .map((role) => `${role.value}`)
                    .indexOf(`${TEAM}`);
                if (
                    teamRefIndex > -1 &&
                    registrationRefs[teamRefIndex] &&
                    registrationRefs[teamRefIndex].current
                ) {
                    registrationRefs[teamRefIndex].current.focus();
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        roleSelected,
        Registration_Role,
        Team,
        teamModalOpen,
        teamModalWasOpen,
    ]);

    useEffect(() => {
        if (teamModalOpen) {
            setTeamModalWasOpen(true);
        }
    }, [teamModalOpen]);

    return isHidden ? null : (
        <CardGroup legend="Choose a Registration Role" colWidth={12}>
            {roles
                // Map over displayable roles and render appropriate UI
                .map((role, i) => {
                    const selected = roleSelected
                        ? role.value === Registration_Role[0]
                        : false;

                    const displayRoleCard =
                        Registration_Role.length === 0 || selected;

                    return (
                        displayRoleCard && (
                            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, react/no-array-index-key
                            <div key={i} onKeyDown={(e) => handleKeyDown(e, i)}>
                                <DetectRoleCard
                                    controlId={controlId}
                                    name="Registration_Role"
                                    selected={selected}
                                    registrationRef={registrationRefs[i]}
                                    {...role}
                                />
                            </div>
                        )
                    );
                })}
        </CardGroup>
    );
};

RegistrationRole.propTypes = {
    controlId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    isHidden: PropTypes.bool,
    roles: PropTypes.arrayOf(PropTypes.shape({})),
    teamModalOpen: PropTypes.bool.isRequired,
};

export default RegistrationRole;
